import React, {useEffect, useRef, useState} from "react";
import MerLogo from "../../assets/logo_mer.png";
import './StartPage.css';
import ConnectorCard, {isPreparing, isSelectable} from "./ConnectorListItem";
import {Charger, Evs, isStatus} from "../../Model/Charger";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {createPaymentRequest, getCharger, getConnectorStatus, getSession} from "../../Api/Api";
import ErrorPage, {ERROR_PAGE_TYPE} from "../Error/ErrorPage";
import {getVippsAppUrl} from "../../Common/AppstoreLink";
import {OrderSession} from "../../Model/Session";
import {
    registerCreatePaymentFailed,
    registerCreatePaymentSuccess,
    registerPlug,
    setLocation
} from "../../Api/GTM";
import {SESSION_PAYMENT_SUCCESS_SENT} from "../Session/SessionPage";
import {isMockMode} from "../../Common/MockMode";
import {formatTariff} from "../../Common/Tariff";

export const MER_ORDER_ID = "MER_SESSION_ID"
export const MER_EVSE_ID = "MER_EVSE_ID";
export const MER_LOCATION_NAME = "MER_LOCATION_NAME";
export const MER_STOP_PRESSED = "MER_STOP_PRESSED";
export const MER_START_CHARGING_SCENARIO = "";
export const RESERVE_AMOUNT = 450;
// The driver is standing at the charger plugging in, so the connector statuses
// need to catch up with them within a couple of seconds
export const CONNECTOR_POLL_INTERVAL_MS = 2000;
enum CHARGER_RESPONSE_STATUS {
    INITIAL,
    NOT_FOUND,
    FOUND,
    ERROR
}

export default function StartPage() {

    const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
    const [charger, setCharger] = useState<{ charger: Charger | undefined, status: CHARGER_RESPONSE_STATUS }>
    ({charger: undefined, status: CHARGER_RESPONSE_STATUS.INITIAL});
    const {evseId} = useParams<{ evseId: string }>();
    const history = useNavigate();
    const location = useLocation();
    // Set by the session page when the driver aborted the payment in Vipps
    const [paymentCancelled] = useState(() => !!(location.state as { paymentCancelled?: boolean } | null)?.paymentCancelled);
    // Once the driver has picked a connector themselves, a poll must not move
    // the selection out from under them, unless a cable is plugged into another one
    const pickedByDriver = useRef(false);
    const sessionLookupStarted = useRef(false);

    // Cleanup local storage
    localStorage.removeItem(MER_STOP_PRESSED);
    localStorage.removeItem(SESSION_PAYMENT_SUCCESS_SENT);


    // Only for the visit it came with, not again on a reload
    useEffect(() => {
        if (paymentCancelled) {
            history(location.pathname, {replace: true, state: null});
        }
        // Runs once on arrival; the replace changes location, which must not re-run it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function selectConnector(connectorId: string) {
        pickedByDriver.current = true;
        setSelectedConnectorId(connectorId);
    }

    // Load the charger once, then poll each connector's live status while this
    // screen is open, so a cable plugged in after the page loaded shows up (and
    // gets preselected) without the driver reloading
    useEffect(() => {
        if (!evseId) {
            setCharger({charger: undefined, status: CHARGER_RESPONSE_STATUS.NOT_FOUND})
            return;
        }

        let stopped = false;
        let inFlight = false;
        let timer: ReturnType<typeof setTimeout>;
        // The charger as loaded, whose statuses are the fallback for connectors
        // without a live status
        let loadedCharger: Charger | undefined;
        // Connectors that had a cable in at the last update, to tell when one is plugged in
        let plugged = new Set<string>();

        const scheduleNext = () => {
            if (!stopped) {
                clearTimeout(timer);
                timer = setTimeout(poll, CONNECTOR_POLL_INTERVAL_MS);
            }
        };

        function showCharger(charger: Charger) {
            setCharger({charger: charger, status: CHARGER_RESPONSE_STATUS.FOUND})
            const connectors = charger.location.evse.connectors;
            // A cable going in says which connector is meant more surely than a tap,
            // so it wins even over a connector the driver picked first
            const newlyPlugged = connectors.find(c => isPreparing(c) && !plugged.has(c.id));
            plugged = new Set(connectors.filter(isPreparing).map(c => c.id));
            if (newlyPlugged) {
                setSelectedConnectorId(newlyPlugged.id);
            } else if (!pickedByDriver.current) {
                // A cable is already plugged in, so that connector is the one meant
                const preparing = connectors.find(isPreparing);
                const selectable = connectors.filter(isSelectable);
                if (preparing) {
                    setSelectedConnectorId(preparing.id);
                } else if (selectable.length === 1) {
                    // Nothing to choose between, so preselect it and go straight to payment
                    setSelectedConnectorId(selectable[0].id);
                } else {
                    setSelectedConnectorId(null);
                }
            } else {
                // The driver's pick went busy or out of order since, so it can no longer be paid for
                setSelectedConnectorId(current =>
                    connectors.some(c => c.id === current && isSelectable(c)) ? current : null);
            }
        }

        function loadCharger(): Promise<void> {
            return getCharger(evseId!).then((response) => {
                if (stopped) {
                    return;
                }
                if (response.data.location && response.data.location.evse) {
                    const charger: Charger = response.data as Charger;
                    charger.location.evse.connectors.forEach((c) => {
                        c.tariff = c.tariff.replace("0.0kr/min+", "");
                        c.tariff_Unreg = c.tariff_Unreg.replace("0.0kr/min+", "");
                    })
                    // Frigg does not return connectors in a fixed order
                    charger.location.evse.connectors.sort((a, b) =>
                        a.smsCode.localeCompare(b.smsCode, undefined, {numeric: true}) || a.id.localeCompare(b.id));
                    loadedCharger = charger;
                    showCharger(charger);
                    localStorage.setItem(MER_EVSE_ID, evseId!)
                    localStorage.setItem(MER_LOCATION_NAME, charger.location.name)
                    setLocation(charger.location.name)
                } else {
                    // The charger does not exist, so trying again will never help
                    stopped = true;
                    setCharger({charger: undefined, status: CHARGER_RESPONSE_STATUS.NOT_FOUND})
                }
            }).catch(() => {
                if (stopped) {
                    return;
                }
                setCharger({charger: undefined, status: CHARGER_RESPONSE_STATUS.ERROR});
                // Keep trying: a dropped connection at the charger usually comes back
                scheduleNext();
            })
        }

        // One request per connector. A connector whose status is null, unrecognised
        // or fails to load keeps the status it was loaded with.
        function refreshStatuses(charger: Charger): Promise<void> {
            return Promise.all(charger.location.evse.connectors.map((connector) => {
                if (!connector.driivzInternalId) {
                    return Promise.resolve(connector);
                }
                return getConnectorStatus(connector.driivzInternalId)
                    .then((response) => {
                        const liveStatus = response.data?.status;
                        return isStatus(liveStatus) ? {...connector, status: liveStatus} : connector;
                    })
                    .catch(() => connector);
            })).then((connectors) => {
                if (stopped) {
                    return;
                }
                showCharger({...charger, location: {...charger.location, evse: {...charger.location.evse, connectors}}});
                scheduleNext();
            });
        }

        // Chained timeouts rather than an interval, so a slow response can never
        // stack requests on top of each other
        function poll() {
            if (stopped || inFlight) {
                return;
            }
            if (!loadedCharger) {
                inFlight = true;
                loadCharger().then(() => {
                    inFlight = false;
                    // Show the live statuses straight away rather than after the first interval
                    if (loadedCharger) {
                        poll();
                    }
                });
                return;
            }
            // No connector has a live status to poll for
            if (!loadedCharger.location.evse.connectors.some(c => c.driivzInternalId)) {
                return;
            }
            // Nothing to refresh while the phone is locked or the tab is in the background
            if (document.hidden) {
                scheduleNext();
                return;
            }
            inFlight = true;
            refreshStatuses(loadedCharger).finally(() => inFlight = false);
        }

        // Coming back to the screen should show the truth straight away rather
        // than whatever was on it two seconds ago
        const pollOnReturn = () => {
            if (!document.hidden) {
                clearTimeout(timer);
                poll();
            }
        };
        document.addEventListener("visibilitychange", pollOnReturn);

        poll();

        return () => {
            stopped = true;
            clearTimeout(timer);
            document.removeEventListener("visibilitychange", pollOnReturn);
        };
    }, [evseId])

    const evs: Evs | undefined = charger.charger?.location.evse;


    // Charger not loaded yet
    if (charger.status === CHARGER_RESPONSE_STATUS.INITIAL) {
        return <></>
    }

    // Charger wasn't found
    if (charger.status === CHARGER_RESPONSE_STATUS.NOT_FOUND) {
        return <ErrorPage errorType={ERROR_PAGE_TYPE.CHARGER_NOT_FOUND}/>;
    }

    // Connection failed
    if (charger.status === CHARGER_RESPONSE_STATUS.ERROR) {
        return <ErrorPage errorType={ERROR_PAGE_TYPE.CONNECTION_ERROR}/>;
    }


    if (!evs || !charger.charger) {
        return <></>;
    }

    // Check if there's an active session, and act upon that session
    const orderId = localStorage.getItem(MER_ORDER_ID);
    if (orderId && !sessionLookupStarted.current) {
        sessionLookupStarted.current = true;
        getSession(orderId).then(res => {
            let session: OrderSession = res.data;
            if(session.session.status === 'COMPLETED'){
                localStorage.removeItem(MER_ORDER_ID);
                history(`/vipps/receipt/${orderId}`);
            }else{
                history(`/vipps/session/active/${orderId}`);
            }
        }).catch(e => {
            localStorage.removeItem(MER_ORDER_ID);
        })
    }

    const selectedConnector = evs.connectors.find(c => c.id === selectedConnectorId);

    function initiatePaymentRequest() {
        if (evs && selectedConnectorId && selectedConnector) {
            registerPlug(selectedConnector.type.toLowerCase());
            createPaymentRequest(selectedConnectorId, RESERVE_AMOUNT,
                selectedConnector.tariff, selectedConnector.tariff_Unreg, selectedConnector.maxEffect).then(resp => {
                let response = resp.data;
                const orderID = response.orderId;
                localStorage.setItem(MER_ORDER_ID, orderID);
                localStorage.setItem(MER_START_CHARGING_SCENARIO, selectedConnector.startChargingScenario)
                window.location.href = response.url
                registerCreatePaymentSuccess();
                if (!isMockMode()) {
                    setTimeout(() => {
                        window.location.href = getVippsAppUrl();
                    }, 5000);
                }
            }).catch((e) => {

                registerCreatePaymentFailed();
            })
        }
    }

    return (
        <div className="app connector-app">
            <header className="connector-topbar">
                <div>
                    <h2>Drop-in med Vipps</h2>
                    <address>{charger.charger.location.name}</address>
                </div>
                <img src={MerLogo} alt="Mer" className="connector-topbar-logo"/>
            </header>

            <div className="connector-page">
                {paymentCancelled &&
                    <p className="payment-cancelled-notice" role="status">
                        Betalingen ble avbrutt. Velg ladeuttak og prøv igjen når du er klar.
                    </p>
                }
                <StepHeading number={1}>Koble ladekabelen til bilen</StepHeading>

                <StepHeading number={2}>Velg tilkoblet ladeuttak</StepHeading>
                <main className="connector-list">
                    {evs.connectors.map(connector =>
                        <ConnectorCard key={connector.id}
                                       connector={connector}
                                       setSelectedChargerId={selectConnector}
                                       selected={connector.id === selectedConnectorId}/>
                    )}
                </main>

                <StepHeading number={3}>Betal for å starte ladingen</StepHeading>
                <section className="connector-card payment-card">
                    {selectedConnector ?
                        <>
                            <h3 className="payment-title">Ladeuttak {selectedConnector.smsCode}</h3>
                            <p className="connector-muted">{selectedConnector.type} &middot; {selectedConnector.maxEffect} kW</p>
                            <p className="connector-muted">Drop-in-pris: {formatTariff(selectedConnector.tariff_Unreg)}</p>
                        </>
                        :
                        <h3 className="payment-placeholder">Velg ladeuttak ovenfor</h3>
                    }
                    <div className="connector-divider"/>
                    <div className="payment-row">
                        <span>Midlertidig reservasjon</span>
                        <span>{RESERVE_AMOUNT} NOK</span>
                    </div>
                    <p className="payment-note">Du betaler bare for det du bruker.</p>
                    <button
                        type="button"
                        disabled={!selectedConnector}
                        onClick={initiatePaymentRequest}
                        className="vipps-button">
                        Betal med Vipps <VippsSmile/>
                    </button>
                </section>
            </div>
        </div>
    );
}

function StepHeading({number, children}: { number: number, children: React.ReactNode }) {
    return (
        <h2 className="connector-step">
            <span className="connector-step-number" aria-hidden="true">{number}</span>
            {children}
        </h2>
    );
}

function VippsSmile() {
    return (
        <svg className="vipps-smile" width="26" height="22" viewBox="0 0 26 22" fill="none" aria-hidden="true">
            <circle cx="16" cy="3.5" r="3" fill="currentColor"/>
            <path d="M2.5 11.5C5.5 15.5 9 17.5 13 17.5C17 17.5 20.5 15.5 23.5 11.5" stroke="currentColor"
                  strokeWidth="4" strokeLinecap="round"/>
        </svg>
    );
}
